// @ts-nocheck
/**
 * /ClientDashboard — the client portal (viewer space; RequireSpace in App.tsx
 * sends every other role to its own space).
 *
 * W5 (B3-ClientDashboard, DUP-4, DUP-6): the only version of the portal. The
 * tab lives in `?tab=` (useTabParam, src/lib/portalTabs.ts); the five former
 * stand-alone pages (ClientCalls, ClientMeetings, ClientPerformance,
 * ClientSettings, ClientHelp) redirect to their tab. Shell: rail (ui_refonte)
 * or DashboardSidebar, both with the mobile menu drawer; the header carries
 * the tab's title as the page's single <h1> (DESIGN.md §5), and the avatar
 * opens the account menu (it used to log out on click).
 * Sections: src/components/backoffice/sections/*.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate, formatDateTime, currentLocale } from '@/i18n/locale';
import { api } from '@/api/api-client';
import { downloadKpiReportPdf } from '@/lib/kpiReportPdf';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useClientAuth } from '@/hooks/useClientAuth';
import { Search, Phone, Calendar, Users, Download, FileText, Eye, Pencil, MoreHorizontal } from 'lucide-react';
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { hasMeetingBooked } from '@/utils/callUtils';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    CommandDialog,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command";

import DashboardSidebar from '@/components/backoffice/DashboardSidebar';
import PortalAccountMenu from '@/components/backoffice/PortalAccountMenu';
import { useTabParam } from '@/hooks/useTabParam';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { PORTAL_TABS } from '@/lib/portalTabs';
import { usePortalAwaitingMeetings } from '@/hooks/usePortalAwaitingMeetings';
import { getUiFlags } from '@/lib/flags';
import { PortalRail } from '@/components/shell/PortalRail';
import { ImpersonationBanner } from '@/components/shell/ImpersonationBanner';
import { RailDrawer, RailDrawerButton, readRailCollapsed, writeRailCollapsed } from '@/components/shell/RailDrawer';
import NotificationsDropdown from '@/components/backoffice/NotificationsDropdown';
import OnboardingWizard from '@/components/backoffice/OnboardingWizard';
import { toast } from 'sonner';

import ProspectingAssistant from '@/components/backoffice/ProspectingAssistant';
import FloatingAudioPlayer from '@/components/common/FloatingAudioPlayer';

import HomeSection from '@/components/backoffice/sections/HomeSection';
import SavedViews from '@/components/backoffice/SavedViews';
import CallsSection from '@/components/backoffice/sections/CallsSection';
import MeetingsSection from '@/components/backoffice/sections/MeetingsSection';
import PerformanceSection from '@/components/backoffice/sections/PerformanceSection';
import SettingsSection from '@/components/backoffice/sections/SettingsSection';
import HelpSection from '@/components/backoffice/sections/HelpSection';
import ProspectsSection from '@/components/backoffice/sections/ProspectsSection';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuthStore } from '@/stores/authStore';
import { getPortalSettings, PORTAL_SETTINGS_QUERY_KEY } from '@/api/endpoints/portal';
import { toCsv, downloadCsv } from '@/lib/exportCsv';
import { ApiErrorException } from '@/api/client';

/**
 * REV-W5-1: where a set of queries stands, for the sections: failed if one
 * failed (with its code and a retry of the failed ones), loading while one
 * loads, else ready. The sections then show skeletons or « Indisponible »,
 * never 0 nor « Aucun appel ».
 */
function loadOf(queries) {
    const failed = queries.filter(q => q.isError);
    if (failed.length > 0) {
        const err = failed[0].error;
        return {
            status: 'error',
            code: err instanceof ApiErrorException ? err.code : 'NETWORK',
            onRetry: () => { failed.forEach(q => { void q.refetch(); }); },
        };
    }
    if (queries.some(q => q.isLoading)) return { status: 'loading' };
    return { status: 'ready' };
}

export default function ClientDashboard() {
    const { t } = useTranslation(['pages', 'common', 'admin']);
    const SECTION_TITLES = {
        home: t('pages:portal.sections.home'),
        calls: t('pages:portal.sections.calls'),
        prospects: t('pages:portal.sections.prospects'),
        meetings: t('pages:portal.sections.meetings'),
        performance: t('pages:portal.sections.performance'),
        settings: t('pages:portal.sections.settings'),
        help: t('pages:portal.sections.help'),
    };
    const { client, isLoading: loading, error: clientError, logout, invalidate } = useClientAuth();
    const [activeTab, setActiveTab] = useTabParam(PORTAL_TABS, 'home');
    useDocumentTitle(SECTION_TITLES[activeTab]);
    const [showWizard, setShowWizard] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    // SR1-flag : le rail ne remplace la barre latérale que derrière ui_refonte.
    const railOn = getUiFlags().ui_refonte;
    const [railCollapsed, setRailCollapsed] = useState(() => readRailCollapsed('portal'));
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [audio, setAudio] = useState({ src: '', title: '' });
    const queryClient = useQueryClient();

    // QA-W5-4: `client` (/auth/client-me) is the ORGANISATION; the signed-in
    // viewer is the auth user, named by its portal profile (Paramètres), which
    // SettingsSection refreshes under the same query key when it is renamed.
    const authUser = useAuthStore((s) => s.user);
    // QA-W7-3: « ouvrir en tant que » is read-only: no automatic write.
    const impersonating = useAuthStore((s) => !!s.impersonation);
    const { data: portalSettings } = useQuery({
        queryKey: PORTAL_SETTINGS_QUERY_KEY,
        queryFn: ({ signal }) => getPortalSettings({ signal }),
        retry: false,
        staleTime: 60_000,
    });
    const viewerEmail = authUser?.email || portalSettings?.profile?.email || '';
    const viewerName = portalSettings?.profile?.full_name || '';

    // Server-persisted "seen tab" markers (DismissedCard entity, card_key
    // "seen-tab:<tab>"). QA-W5-1: a marker is the viewer's own (the server
    // stamps caller_email), so the read asks for the viewer's markers.
    const seenTabsKey = ['client-dashboard-seen-tabs', viewerEmail];
    const { data: seenTabRows = [], isSuccess: seenTabsLoaded } = useQuery({
        queryKey: seenTabsKey,
        queryFn: () => api.entities.DismissedCard.filter({ caller_email: viewerEmail }, '-dismissed_at', 200),
        enabled: !!viewerEmail,
    });
    const seenTabs = React.useMemo(() => {
        const out = {};
        for (const r of seenTabRows) {
            if (r.caller_email && r.caller_email !== viewerEmail) continue;
            if (r.card_key?.startsWith('seen-tab:')) out[r.card_key.slice('seen-tab:'.length)] = true;
        }
        return out;
    }, [seenTabRows, viewerEmail]);
    // A tab is marked once per visit of the page, even when the write fails
    // (QA-W5-1: a 409 used to repost on every render).
    const markedTabs = React.useRef(new Set());
    const handlePlayRecording = (url, title) => setAudio({ src: url, title });

    // AUDIT-ADM-03: the wizard writes the organisation profile (PATCH
    // /api/tenant/profile), which only a tenant admin may do. The portal only
    // admits viewers, so in practice it never shows. W5: the settings tab is
    // the viewer's own (profile, preferences), shown to every portal user.
    const { isTenantAdmin } = usePermissions();
    useEffect(() => {
        if (client && !client.contact_name && isTenantAdmin) {
            setShowWizard(true);
        }
    }, [client, isTenantAdmin]);

    const callLogsQuery = useQuery({
        queryKey: ['call-logs', client?.email],
        queryFn: () => api.entities.CallLog.filter({}, '-call_date'),
        enabled: !!client?.email,
    });

    const parallelCallsQuery = useQuery({
        queryKey: ['parallel-calls-completed', client?.email],
        queryFn: () => api.entities.ParallelCall.filter({
            call_status: { $in: ['completed', 'connected_to_agent'] }
        }, '-started_at'),
        enabled: !!client?.email,
    });

    const callLogs = callLogsQuery.data ?? [];
    const parallelCalls = parallelCallsQuery.data ?? [];
    const callsLoad = loadOf([callLogsQuery, parallelCallsQuery]);

    // REV-W5-6: the same « RDV à valider » list as the home card and the
    // Mes rendez-vous tab (one filter, one upcoming rule).
    const { rows: awaitingMeetings } = usePortalAwaitingMeetings({ scope: client?.email, enabled: !!client?.email });

    // Merge both call types into one array
    const baseCalls = [
        ...callLogs.map(c => ({
            ...c,
            _source: 'CallLog',
            _agent: c._agent || '—',
            is_connected: (c.duration_seconds || 0) > 0,
        })),
        ...parallelCalls.map(pc => ({
            id: pc.id,
            prospect_name: pc.prospect_name,
            prospect_company: pc.prospect_company,
            prospect_title: '',
            call_date: pc.started_at,
            duration_seconds: pc.duration_seconds,
            outcome: pc.call_outcome || pc.call_status,
            notes: pc.notes,
            recording_url: null,
            meeting_date: pc.connected_at,
            meeting_status: pc.meeting_status,
            _source: 'ParallelDialer',
            _agent: pc.agent_name || '—',
            is_connected: pc.call_status === 'connected_to_agent' || (pc.duration_seconds || 0) > 0,
        }))
    ].sort((a, b) => new Date(b.call_date) - new Date(a.call_date));

    // Derived options
    const agentOptions = React.useMemo(() => Array.from(new Set(baseCalls.map(c => c._agent).filter(Boolean))), [baseCalls]);

    // Filters state
    const [filterFrom, setFilterFrom] = useState('');
    const [filterTo, setFilterTo] = useState('');
    const [filterSource, setFilterSource] = useState('all');
    const [filterAgent, setFilterAgent] = useState('all');

    // Persistence of the calls tab's filters via SavedView entity.
    // scope = `dashboard-filters-calls`, name = `__current` (one row per client).
    // REV-W5-2: only the calls tab shows filters (Performance has its own
    // period), so only its saved filters are read; an old
    // `dashboard-filters-performance` row is ignored.
    const FILTER_TABS = ['calls'];
    const filterScope = FILTER_TABS.includes(activeTab) ? `dashboard-filters-${activeTab}` : null;

    const filterViewQuery = useQuery({
        queryKey: ['dashboard-filters', client?.email, filterScope],
        queryFn: () => api.entities.SavedView.filter({ scope: filterScope, name: '__current' }, '-created_date', 1),
        enabled: !!client?.email && !!filterScope,
    });
    // REV-W5-11: hydrate once the saved row is read (it used to hydrate from
    // the empty first render, so the saved filters never came back and were
    // overwritten), and save only a real change (each save's read-back, with
    // a new updated_date, used to trigger the next save, endlessly).
    const filterRowRef = React.useRef(null);
    filterRowRef.current = filterViewQuery.data?.[0] ?? null;
    const filterHydratedRef = React.useRef(null);
    const filterSavedRef = React.useRef(null);
    useEffect(() => {
        if (!filterScope || !filterViewQuery.isSuccess) return;
        const key = `${client?.email}:${filterScope}`;
        if (filterHydratedRef.current === key) return;
        const f = filterRowRef.current?.filters || {};
        const restored = { from: f.from || '', to: f.to || '', source: f.source || 'all', agent: f.agent || 'all' };
        setFilterFrom(restored.from);
        setFilterTo(restored.to);
        setFilterSource(restored.source);
        setFilterAgent(restored.agent);
        filterSavedRef.current = JSON.stringify(restored);
        filterHydratedRef.current = key;
    }, [filterScope, filterViewQuery.isSuccess, client?.email]);

    useEffect(() => {
        if (!filterScope || !client?.email) return;
        const key = `${client.email}:${filterScope}`;
        if (filterHydratedRef.current !== key) return; // not restored yet
        const filters = { from: filterFrom, to: filterTo, source: filterSource, agent: filterAgent };
        const json = JSON.stringify(filters);
        if (json === filterSavedRef.current) return; // nothing changed
        filterSavedRef.current = json;
        const row = filterRowRef.current;
        const promise = row?.id
            ? api.entities.SavedView.update(row.id, { filters })
            : api.entities.SavedView.create({ scope: filterScope, name: '__current', filters });
        promise
            .then(() => queryClient.invalidateQueries({ queryKey: ['dashboard-filters', client.email, filterScope] }))
            .catch(() => {});
    }, [filterFrom, filterTo, filterSource, filterAgent, filterScope, client?.email, queryClient]);

    // REV-W5-2: the filters apply only where they are shown (calls tab).
    // Elsewhere, Actions › Exporter CSV / Rapport take every call.
    const filtersShown = activeTab === 'calls';
    const filtersActive = filtersShown && (!!filterFrom || !!filterTo || filterSource !== 'all' || filterAgent !== 'all');

    // Compute filtered calls
    const filteredCalls = baseCalls.filter(c => {
        const d = new Date(c.call_date);
        if (filterFrom && d < new Date(filterFrom)) return false;
        if (filterTo && d > new Date(filterTo)) return false;
        if (filterSource !== 'all' && c._source !== filterSource) return false;
        if (filterAgent !== 'all' && (c._agent || '—') !== filterAgent) return false;
        return true;
    });

    const { data: prospects = [] } = useQuery({
        queryKey: ['prospects', client?.email],
        queryFn: () => api.entities.ProspectQueue.filter({}, '-created_date', 300),
        enabled: !!client?.email,
    });

    const metricsQuery = useQuery({
        queryKey: ['metrics', client?.email],
        queryFn: () => api.entities.ClientMetrics.filter({}, '-period'),
        enabled: !!client?.email,
    });
    const metrics = metricsQuery.data ?? [];
    const metricsLoad = loadOf([metricsQuery]);

    const handleLogout = () => logout();

    // Keyboard shortcut for search
    useEffect(() => {
        const down = (e) => {
            if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                setSearchOpen((open) => !open);
            }
        };
        document.addEventListener('keydown', down);
        return () => document.removeEventListener('keydown', down);
    }, []);

    // Mark current tab as seen to hide badges after visiting
    useEffect(() => {
        if (!activeTab || !viewerEmail || !seenTabsLoaded || impersonating) return;
        if (seenTabs[activeTab] || markedTabs.current.has(activeTab)) return;
        markedTabs.current.add(activeTab);
        const key = ['client-dashboard-seen-tabs', viewerEmail];
        api.entities.DismissedCard.create({
            card_key: `seen-tab:${activeTab}`,
            dismissed_at: new Date().toISOString(),
        })
            .then((row) => queryClient.setQueryData(key, (old = []) => [...old, row]))
            // Already marked (another window): read the markers back once.
            .catch(() => queryClient.invalidateQueries({ queryKey: key }));
    }, [activeTab, viewerEmail, seenTabsLoaded, seenTabs, queryClient, impersonating]);

    // Export data to CSV
    const handleExport = useCallback(() => {
        const headers = [t('pages:portal.dashboard.csv.prospect'), t('pages:portal.dashboard.csv.company'), t('pages:portal.dashboard.csv.date'), t('pages:portal.dashboard.csv.durationSeconds'), t('pages:portal.dashboard.csv.outcome'), t('pages:portal.dashboard.csv.notes')];
        const rows = (filtersShown ? filteredCalls : baseCalls).map(call => [
            call.prospect_name,
            call.prospect_company,
            formatDate(call.call_date),
            call.duration_seconds || 0,
            call.outcome,
            call.notes || ''
        ]);
        
        downloadCsv(`appels_${client?.company_name || 'export'}_${new Date().toISOString().split('T')[0]}.csv`, toCsv(headers, rows));
    }, [filtersShown, filteredCalls, baseCalls, client, t]);

    // Generate PDF report
    // QA-W5-9: a loading toast while the report is built, a translated error
    // (with the server's code) when it fails, never a silent rejection.
    const [reportBusy, setReportBusy] = useState(false);
    const handleGenerateReport = useCallback(async () => {
        if (reportBusy) return;
        const from = (filtersShown && filterFrom) || null;
        const to = (filtersShown && filterTo) || null;
        const source = filtersShown ? filterSource : 'all';
        const agent = filtersShown ? filterAgent : 'all';

        setReportBusy(true);
        const toastId = toast.loading(t('pages:portal.dashboard.reportGenerating'));
        try {
            const { data } = await api.functions.invoke('generateKPIReport', {
                client_email: client?.email,
                from, to, source, agent
            });
            // The route answers the KPIs as JSON: the PDF is built here (AUDIT-F17).
            await downloadKpiReportPdf(data, {
                from, to, source, t, locale: currentLocale(),
                fileName: `rapport_${client?.company_name || 'client'}_${new Date().toISOString().split('T')[0]}.pdf`,
            });
            toast.success(t('pages:portal.dashboard.reportReady'), { id: toastId });
        } catch (err) {
            const code = err instanceof ApiErrorException ? err.code : 'NETWORK';
            toast.error(t('pages:portal.dashboard.reportError'), {
                id: toastId,
                description: <span className="font-mono text-[11px]">{code}</span>,
            });
        } finally {
            setReportBusy(false);
        }
    }, [reportBusy, client, filtersShown, filterFrom, filterTo, filterSource, filterAgent, t]);

    // Search results
    const searchResults = searchQuery.length > 0 ? {
        calls: baseCalls.filter(c => 
            c.prospect_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            c.prospect_company?.toLowerCase().includes(searchQuery.toLowerCase())
        ).slice(0, 5),
        prospects: (prospects || []).filter(p =>
            p.prospect_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            p.prospect_company?.toLowerCase().includes(searchQuery.toLowerCase())
        ).slice(0, 5),
        meetings: baseCalls.filter(c => (hasMeetingBooked(c) && c.meeting_date)).slice(0, 5),
        sections: [
            { id: 'home', label: SECTION_TITLES.home },
            { id: 'prospects', label: SECTION_TITLES.prospects },
            { id: 'calls', label: SECTION_TITLES.calls },
            { id: 'meetings', label: SECTION_TITLES.meetings },
            { id: 'performance', label: SECTION_TITLES.performance },
            { id: 'settings', label: SECTION_TITLES.settings },
            { id: 'help', label: SECTION_TITLES.help },
        ].filter(s => s.label.toLowerCase().includes(searchQuery.toLowerCase()))
    } : { calls: [], prospects: [], meetings: [], sections: [] };

    // Sidebar badges: missed calls, pending prospects, pending meeting validations
    const missedCalls = baseCalls.filter(c => ['no_answer', 'busy', 'voicemail'].includes(String(c.outcome || '').toLowerCase())).length;
    const pendingProspects = (prospects || []).filter(p => (p.status || 'pending') === 'pending').length;
    const pendingValidations = awaitingMeetings.length;

    const navBadges = {
        calls: (!seenTabs?.calls && missedCalls > 0) ? missedCalls : null,
        prospects: (!seenTabs?.prospects && pendingProspects > 0) ? pendingProspects : null,
        meetings: (!seenTabs?.meetings && pendingValidations > 0) ? pendingValidations : null,
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center text-gray-900" role="status" aria-label={t('common:actions.loading')}>
                <div className="animate-spin motion-reduce:animate-none rounded-full h-10 w-10 border-2 border-gray-200 border-t-gray-900" />
            </div>
        );
    }

    // No client profile (client-me failed): say so, instead of an empty
    // « Bonjour Client » dashboard at zero (audit B3-ClientDashboard, 2).
    if (!client) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 text-gray-900">
                <div className="max-w-sm text-center" role="alert">
                    <h1 className="text-lg font-semibold">{t('pages:portal.unavailable.title')}</h1>
                    <p className="mt-2 text-sm text-gray-600">{t('pages:portal.unavailable.body')}</p>
                    {clientError?.code && <p className="mt-2 font-mono text-xs text-gray-500">{clientError.code}</p>}
                    <Button className="mt-4 min-h-11" onClick={invalidate}>{t('common:actions.retry')}</Button>
                </div>
            </div>
        );
    }

    const renderSection = () => {
        switch (activeTab) {
            case 'home':
                return <HomeSection client={client} viewerName={viewerName} calls={baseCalls} callsLoad={callsLoad} onTabChange={setActiveTab} onPlayRecording={handlePlayRecording} />;
            case 'calls':
                return <CallsSection calls={filteredCalls} load={callsLoad} clientEmail={client?.email} onPlayRecording={handlePlayRecording} currentAudioSrc={audio.src} />;
            case 'prospects':
                return <ProspectsSection client={client} />;
            case 'meetings':
                return <MeetingsSection clientEmail={client?.email} />;
            case 'performance':
                return <PerformanceSection calls={baseCalls} metrics={metrics} load={callsLoad} metricsLoad={metricsLoad} />;
            case 'settings':
                return <SettingsSection client={client} onUpdate={invalidate} />;
            case 'help':
                return <HelpSection />;
            default:
                return null;
        }
    };

    const goTo = (id) => { setDrawerOpen(false); setActiveTab(id); };

    return (
        <div className="min-h-screen bg-gray-50 flex text-gray-900">
            {railOn ? (
                <div className="hidden lg:block sticky top-0 h-screen">
                    <PortalRail activeTab={activeTab} onTabChange={setActiveTab} companyName={client?.company_name} badges={navBadges} collapsed={railCollapsed} onCollapsedChange={(c) => { setRailCollapsed(c); writeRailCollapsed('portal', c); }} />
                </div>
            ) : (
                <DashboardSidebar activeTab={activeTab} onTabChange={setActiveTab} client={client} onCollapsedChange={setSidebarCollapsed} badges={navBadges} />
            )}
            {/* Under 1024 px: the same navigation in a drawer, in both shells (W5). */}
            <RailDrawer open={drawerOpen} onOpenChange={setDrawerOpen} title={t('admin:backoffice.sidebar.navLabel')}>
                {railOn ? (
                    <PortalRail activeTab={activeTab} onTabChange={goTo} companyName={client?.company_name} badges={navBadges} collapsed={false} onCollapsedChange={() => {}} collapsible={false} />
                ) : (
                    <DashboardSidebar variant="drawer" activeTab={activeTab} onTabChange={goTo} client={client} badges={navBadges} />
                )}
            </RailDrawer>

            <div className={`flex-1 min-w-0 ${railOn ? '' : sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-56'}`}>
                {/* QA-W5-6: the toasts come from App's single Sonner Toaster (two showed each toast twice). */}
                <header className="sticky top-0 z-20 bg-white border-b border-gray-200">
                    {/* REV-W5-3: below sm every control is a 44 px icon (« Actions » too)
                        and the gaps shrink, so the title (the page's <h1>) stays readable at 390 px. */}
                    <div className="flex items-center gap-1 h-16 px-3 sm:gap-3 sm:px-6">
                        <RailDrawerButton breakpoint="lg" onOpen={() => setDrawerOpen(true)} />
                        <h1 className="min-w-0 truncate text-base font-semibold text-gray-900 sm:text-lg" data-testid="portal-title">{SECTION_TITLES[activeTab]}</h1>
                        <div className="flex-1" />
                        <button
                            type="button"
                            onClick={() => setSearchOpen(true)}
                            aria-label={t('pages:portal.dashboard.searchPlaceholder')}
                            className="flex h-11 min-w-11 items-center justify-center gap-2 rounded-md border border-gray-200 bg-white px-3 text-left text-sm text-gray-500 hover:bg-gray-50 md:w-64 md:justify-start"
                        >
                            <Search className="h-4 w-4 shrink-0 text-gray-400" aria-hidden />
                            <span className="hidden flex-1 md:inline">{t('common:actions.search')}</span>
                            {/* i18n-exempt: key names, the same in every language */}
                            <kbd className="hidden md:inline-flex h-5 items-center gap-1 rounded border bg-white px-1.5 text-[10px] font-medium text-gray-400">Ctrl K</kbd>
                        </button>
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button
                                    type="button"
                                    aria-label={t('pages:portal.dashboard.actions')}
                                    data-testid="portal-actions"
                                    className="flex h-11 w-11 shrink-0 items-center justify-center gap-2 rounded-md border border-gray-200 bg-white text-sm font-medium text-gray-900 hover:bg-gray-50 sm:w-auto sm:px-3"
                                >
                                    <MoreHorizontal className="h-4 w-4 sm:hidden" aria-hidden />
                                    <span className="hidden sm:inline">{t('pages:portal.dashboard.actions')}</span>
                                    <svg className="hidden w-3 h-3 sm:block" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                                <DropdownMenuItem onClick={() => setActiveTab('calls')}>
                                    <Phone className="w-4 h-4 mr-2" aria-hidden />
                                    {t('pages:portal.dashboard.viewCalls')}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => setActiveTab('meetings')}>
                                    <Calendar className="w-4 h-4 mr-2" aria-hidden />
                                    {t('pages:portal.dashboard.viewMeetings')}
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                {filtersActive && (
                                    <p className="px-2 pb-1 pt-1.5 text-xs text-gray-500" data-testid="portal-actions-filters">{t('pages:portal.dashboard.filtersApplied')}</p>
                                )}
                                <DropdownMenuItem onClick={handleExport}>
                                    <Download className="w-4 h-4 mr-2" aria-hidden />
                                    {t('pages:portal.dashboard.exportCsv')}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={handleGenerateReport} disabled={reportBusy} aria-busy={reportBusy}>
                                    <FileText className="w-4 h-4 mr-2" aria-hidden />
                                    {t('pages:portal.dashboard.generateReport')}
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                        <NotificationsDropdown clientEmail={client?.email} />
                        <PortalAccountMenu name={viewerName} email={viewerEmail} onNavigate={setActiveTab} onLogout={handleLogout} />
                    </div>
                </header>

                {/* QA-W5-7: pb-24 keeps the end of every tab clear of the floating assistant button. */}
                <main className="px-4 pt-6 pb-24 sm:px-6 md:px-8 md:pt-8">
                    {/* SU-1 / DES-14: « ouvrir en tant que » banner, the portal's only banner. */}
                    <ImpersonationBanner />
                    {/* Filters for the calls tab; Performance has its own period (one period per page). */}
                    {activeTab === 'calls' && (
                    <div className="mb-6 bg-white rounded-lg border border-gray-200 p-3 flex flex-wrap gap-2 items-center">
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">{t('pages:portal.dashboard.filters.from')}</span>
                            <Input type="date" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} className="h-9 w-40" />
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">{t('pages:portal.dashboard.filters.to')}</span>
                            <Input type="date" value={filterTo} onChange={(e) => setFilterTo(e.target.value)} className="h-9 w-40" />
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">{t('pages:portal.dashboard.filters.source')}</span>
                            <Select value={filterSource} onValueChange={setFilterSource}>
                                <SelectTrigger className="h-9 w-44">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{t('pages:portal.dashboard.filters.allSources')}</SelectItem>
                                    <SelectItem value="CallLog">{t('pages:portal.dashboard.filters.sourceCallLog')}</SelectItem>
                                    <SelectItem value="ParallelDialer">{t('pages:portal.dashboard.filters.sourceDialer')}</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">{t('pages:portal.dashboard.filters.agent')}</span>
                            <Select value={filterAgent} onValueChange={setFilterAgent}>
                                <SelectTrigger className="h-9 w-48">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{t('common:states.all')}</SelectItem>
                                    {agentOptions.map(a => (
                                        <SelectItem key={a} value={a}>{a}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => { setFilterFrom(''); setFilterTo(''); setFilterSource('all'); setFilterAgent('all'); }}>{t('pages:portal.dashboard.filters.reset')}</Button>
                        {/* Saved Views */}
                        <SavedViews
                          clientEmail={client?.email}
                          filters={{ from: filterFrom, to: filterTo, source: filterSource, agent: filterAgent }}
                          onApply={(v) => { setFilterFrom(v.from || ''); setFilterTo(v.to || ''); setFilterSource(v.source || 'all'); setFilterAgent(v.agent || 'all'); }}
                        />
                    </div>
                    )}
                    {renderSection()}
                </main>
            </div>

            <FloatingAudioPlayer src={audio.src} title={audio.title} onClose={() => setAudio({ src: '', title: '' })} />

            <ProspectingAssistant client={client} />

            {showWizard && (
                <OnboardingWizard
                    client={client}
                    onUpdate={invalidate}
                    onComplete={() => setShowWizard(false)}
                />
            )}

            {/* Search Dialog */}
            <CommandDialog open={searchOpen} onOpenChange={setSearchOpen}>
                <CommandInput 
                    placeholder={t('pages:portal.dashboard.searchPlaceholder')} 
                    value={searchQuery}
                    onValueChange={setSearchQuery}
                />
                <CommandList>
                    <CommandEmpty>{t('pages:portal.dashboard.noResults')}</CommandEmpty>
                    {searchResults.sections.length > 0 && (
                        <CommandGroup heading={t('pages:portal.dashboard.groups.sections')}>
                            {searchResults.sections.map((section) => (
                                <CommandItem
                                    key={section.id}
                                    onSelect={() => {
                                        setActiveTab(section.id);
                                        setSearchOpen(false);
                                        setSearchQuery('');
                                    }}
                                >
                                    {section.label}
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    )}
                    {searchResults.calls.length > 0 && (
                        <CommandGroup heading={SECTION_TITLES.calls}>
                            {searchResults.calls.map((call) => (
                                <CommandItem
                                    key={call.id}
                                    onSelect={() => {
                                        setActiveTab('calls');
                                        setSearchOpen(false);
                                        setSearchQuery('');
                                    }}
                                >
                                    <Phone className="w-4 h-4 mr-2 text-gray-400" />
                                    <span>{call.prospect_name}</span>
                                    <span className="ml-2 text-gray-400 text-sm">{call.prospect_company}</span>
                                    <div className="ml-auto flex items-center gap-1">
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="h-6 w-6"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setActiveTab('calls');
                                            setSearchOpen(false);
                                            setSearchQuery('');
                                          }}
                                        >
                                          <Eye className="w-3.5 h-3.5 text-gray-500" />
                                        </Button>
                                    </div>
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    )}
                    {searchResults.prospects.length > 0 && (
                        <CommandGroup heading={SECTION_TITLES.prospects}>
                            {searchResults.prospects.map((p) => (
                                <CommandItem
                                    key={p.id}
                                    onSelect={() => {
                                        setActiveTab('prospects');
                                        setSearchOpen(false);
                                        setSearchQuery('');
                                    }}
                                >
                                    <Users className="w-4 h-4 mr-2 text-gray-400" />
                                    <span>{p.prospect_name}</span>
                                    <span className="ml-2 text-gray-400 text-sm">{p.prospect_company}</span>
                                    <div className="ml-auto flex items-center gap-1">
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="h-6 w-6"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setActiveTab('prospects');
                                            setSearchOpen(false);
                                            setSearchQuery('');
                                          }}
                                        >
                                          <Pencil className="w-3.5 h-3.5 text-gray-500" />
                                        </Button>
                                    </div>
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    )}
                    {searchResults.meetings.length > 0 && (
                        <CommandGroup heading={SECTION_TITLES.meetings}>
                            {searchResults.meetings.map((m) => (
                                <CommandItem
                                    key={m.id}
                                    onSelect={() => {
                                        setActiveTab('meetings');
                                        setSearchOpen(false);
                                        setSearchQuery('');
                                    }}
                                >
                                    <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                                    <span>{m.prospect_name}</span>
                                    <span className="ml-2 text-gray-400 text-sm">{formatDateTime(m.meeting_date || m.call_date)}</span>
                                    <div className="ml-auto flex items-center gap-1">
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="h-6 w-6"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setActiveTab('meetings');
                                            setSearchOpen(false);
                                            setSearchQuery('');
                                          }}
                                        >
                                          <Eye className="w-3.5 h-3.5 text-gray-500" />
                                        </Button>
                                    </div>
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    )}
                    {searchQuery.length === 0 && (
                        <CommandGroup heading={t('pages:portal.dashboard.groups.quickNav')}>
                            <CommandItem onSelect={() => { setActiveTab('home'); setSearchOpen(false); }}>
                                {SECTION_TITLES.home}
                            </CommandItem>
                            <CommandItem onSelect={() => { setActiveTab('calls'); setSearchOpen(false); }}>
                                {SECTION_TITLES.calls}
                            </CommandItem>
                            <CommandItem onSelect={() => { setActiveTab('prospects'); setSearchOpen(false); }}>
                                {SECTION_TITLES.prospects}
                            </CommandItem>
                            <CommandItem onSelect={() => { setActiveTab('meetings'); setSearchOpen(false); }}>
                                {SECTION_TITLES.meetings}
                            </CommandItem>
                            <CommandItem onSelect={() => { setActiveTab('performance'); setSearchOpen(false); }}>
                                {SECTION_TITLES.performance}
                            </CommandItem>
                            <CommandItem onSelect={() => { setActiveTab('settings'); setSearchOpen(false); }}>
                                {SECTION_TITLES.settings}
                            </CommandItem>
                            <CommandItem onSelect={() => { setActiveTab('help'); setSearchOpen(false); }}>
                                {SECTION_TITLES.help}
                            </CommandItem>
                        </CommandGroup>
                    )}
                </CommandList>
            </CommandDialog>
        </div>
    );
}